import { AxiosResponse } from "axios";

import request from "../libs/request";

import { SatisfactionRequest } from "./userReportServices";

const baseUrl = "etc";

export type GetVersionResponse = {
  appVersionId: number;
  osType: string;
  version: string;
  isForceUpdate: true;
  releaseNote: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
};

export const getVersion = (os: string, version: string) =>
  request.get<GetVersionResponse>(`${baseUrl}/app/version/${os}/${version}`);

export type TermTypeKorean =
  | "이용안내"
  | "서비스 이용약관"
  | "위치 기반 서비스 이용 약관"
  | "개인정보 처리 방침"
  | "영상 정보 처리 기기 운영 방침"
  | "청소년 보호 정책"
  | "서비스 이용 동의"
  | "전자 금융 거래 이용 약관"
  | "회의실 약관";

export const termsList = [
  {
    termsCategoryId: 1,
    name: "이용안내",
  },
  {
    termsCategoryId: 2,
    name: "서비스 이용약관",
  },
  {
    termsCategoryId: 3,
    name: "위치 기반 서비스 이용 약관",
  },
  {
    termsCategoryId: 4,
    name: "개인정보 처리 방침",
  },
  {
    termsCategoryId: 5,
    name: "영상 정보 처리 기기 운영 방침",
  },
  {
    termsCategoryId: 6,
    name: "청소년 보호 정책",
  },
  {
    termsCategoryId: 7,
    name: "서비스 이용 동의",
  },
  {
    termsCategoryId: 8,
    name: "전자 금융 거래 이용 약관",
  },
  {
    termsCategoryId: 9,
    name: "회의실 약관",
  },
];

export type GetTermsResponse = {
  categoryName: string;
  content: string;
  createdAt: string;
  createdBy: number;
  deletedAt: string;
  deletedBy: number;
  description: string;
  isUse: boolean;
  modifiedAt: string | null;
  modifiedBy: number;
  name: string;
  postAt: string;
  purpose: string;
  termsCategoryId: number;
  version: string;
  termsId: number;
};

export const getTerms = (
  termType: TermTypeKorean
): Promise<AxiosResponse<GetTermsResponse>> => {
  const term = termsList.find((term) => term.name === termType);
  return request.get<GetTermsResponse>(
    `${baseUrl}/terms/${term?.termsCategoryId}`
  );
};

export type PublicTermPurpose =
  | "personal_gather" //: 개인정보 수집 및 이용
  | "service" //: 서비스 이용 약관
  | "location" //: 위치 기반 이용 약관
  | "skt_personal_use" //: skt_개인 정보 이용 동의
  | "skt_self_verification" //: skt_고유 식별 정보 처리 동의
  | "skt_identification" //: skt_통신사 이용 약관
  | "kt_personal_use" //: kt_개인 정보 이용 동의
  | "kt_self_verification" //: kt_고유 식별 정보 처리 동의
  | "kt_identification" //: kt_통신사 이용 약관
  | "lgt_personal_use" //: lgt_개인 정보 이용 동의
  | "lgt_self_verification" //: lgt_고유 식별 정보 처리 동의
  | "lgt_identification" //: lgt_통신사 이용 약관
  | "common_service" //: 공통_서비스 이용 약관 동의
  | "openSource" //: 오픈소스 라이선스
  | "personal_process" //: 개인정보 처리 방침
  | "marketing"; //: 마케팅 정보 수신 동의

export type GetPublicTermsResponse = {
  categoryName: string;
  content: string;
  createdAt: string;
  createdBy: number;
  deletedAt: string | null;
  deletedBy: number;
  description: string;
  isUse: boolean;
  modifiedAt: string | null;
  modifiedBy: number;
  name: string;
  postAt: string;
  purpose: string;
  termsCategoryId: number;
  version: string;
  termsId: number;
};

export const getTermsById = (termsId: number) =>
  request.get<GetPublicTermsResponse>(`${baseUrl}/terms/${termsId}`);

export type TermsVersion = {
  termsId: number;
  version: string;
};

export const getTermsVersion = (purpose: PublicTermPurpose) =>
  request.get<TermsVersion[]>(`${baseUrl}/terms/version/${purpose}`);

export const getPublicTerms = (purpose: PublicTermPurpose) =>
  request.get<GetPublicTermsResponse>(`${baseUrl}/terms/purpose/${purpose}`);

export type ServiceType =
  | "incommodity"
  | "rental"
  | "space"
  | "cafeteria"
  | "goodsRequest"
  | "hotColdVote";

export type postEtcSatisfactionRequest = SatisfactionRequest & {
  serviceName: ServiceType;
};

export const postEtcSatisfaction = (
  serviceId: string,
  params: postEtcSatisfactionRequest
) => request.post(`${baseUrl}/satisfaction/${serviceId}`, params);

export const patchEtcSatisfaction = (
  etcSatisfaction: number,
  params: SatisfactionRequest
) => request.patch(`${baseUrl}/satisfaction/${etcSatisfaction}`, params);

export const postEtcSatisfactionReply = (
  serviceId: string,
  params: Omit<postEtcSatisfactionRequest, "satisfaction">
) => request.post(`${baseUrl}/satisfaction/reply/${serviceId}`, params);

export const patchEtcSatisfactionReply = (
  serviceId: string,
  params: Omit<SatisfactionRequest, "satisfaction">
) => request.patch(`${baseUrl}/satisfaction/reply/${serviceId}`, params);

export type PostAgreementPurposeRequest = {
  agreedPurpose: string[];
  advertise: boolean;
};

export const agreementPurpose = (params: PostAgreementPurposeRequest) =>
  request.post(`${baseUrl}/terms/agreement/purpose`, params);

export type AgreementRequest = {
  agreement: boolean;
};

export const patchAgreementTerms = (
  termsId: number,
  params: AgreementRequest
) => request.patch(`${baseUrl}/terms/agreement/${termsId}`, params);

export type AppVersion = {
  appVersionId: number;
  osType: string;
  version: string;
  isForceUpdate: boolean;
  releaseNote: string;
  createdBy: number;
  createdByName: string | null;
  createdByEmail: string | null;
  createdAt: string;
};

export const getAppVersion = (os: string, version: string) =>
  request.get<AppVersion>(
    `${baseUrl}/app/version/${os.toLowerCase()}/${version}`
  );

export type HolidayCheckRequest = {
  date: string;
};

export type HolidayCheckResponse = {
  date: string;
  isHoliday: boolean;
};

export const getHolidayCheck = (params: HolidayCheckRequest) =>
  request.get<HolidayCheckResponse>(`${baseUrl}/holiday/check`, { params });

export const getSspParams = () => request.get(`/ssp`);
